import { useEffect, useState, type ReactNode } from "react";
import {
  Activity,
  BriefcaseBusiness,
  CalendarDays,
  ChevronDown,
  Eye,
  EyeOff,
  LogOut,
  MessageCircleMore,
  MessagesSquare,
  MessageSquareText,
  MoreHorizontal,
  ListChecks,
  Settings2,
  UsersRound
} from "lucide-react";
import type { Session } from "../api.js";
import { Avatar } from "./ui.js";

export type AppView = "overview" | "ops" | "work" | "calendar" | "clients" | "activity" | "conversations" | "whatsapp" | "settings";
export type OpsWorkspaceMode = "today" | "calendar" | "board" | "approvals";

interface NavigationItem {
  id: AppView;
  label: string;
  icon: typeof BriefcaseBusiness;
  mobileLabel?: string;
  managerOnly?: boolean;
}

interface OpsNavigationItem {
  id: OpsWorkspaceMode;
  label: string;
  railLabel?: string;
  icon: typeof BriefcaseBusiness;
}

const OPS_NAVIGATION: readonly OpsNavigationItem[] = [
  // WHY [BOL-OPS-CHAT-NAME-001]: this route is the persistent Ops conversation,
  // not a dated briefing. Keep its internal `today` id so history/state remain
  // stable, but name the same destination Ops Chat in desktop, mobile, and the
  // workspace header. Falsifier: any route-level label still says “Today,” or
  // this naming-only correction remounts/forks the conversation.
  { id: "today", label: "Ops Chat", icon: MessageSquareText },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "board", label: "Tasks", icon: BriefcaseBusiness },
  { id: "approvals", label: "Approvals", icon: ListChecks }
];

const MORE_NAVIGATION: readonly NavigationItem[] = [
  // Pairing and identity maintenance are occasional administration, not part
  // of the manager's daily approval loop. Keeping WhatsApp here preserves the
  // full QR/reconnect surface without making the operator think it is daily work.
  { id: "clients", label: "Clients & projects", mobileLabel: "Clients", icon: UsersRound },
  { id: "conversations", label: "Conversations", icon: MessagesSquare, managerOnly: true },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircleMore, managerOnly: true },
  { id: "activity", label: "Activity", icon: Activity },
  { id: "settings", label: "Settings", icon: Settings2 }
];

const VIEW_LABELS: Record<AppView, string> = {
  overview: "Ops Agent",
  ops: "Ops Agent",
  work: "Tasks",
  calendar: "Calendar",
  clients: "Clients & projects",
  activity: "Activity",
  conversations: "Conversations",
  whatsapp: "WhatsApp",
  settings: "Settings"
};

function navigationFor(canManage: boolean) {
  return {
    primaryNavigation: OPS_NAVIGATION,
    moreNavigation: MORE_NAVIGATION.filter((item) => !item.managerOnly || canManage)
  };
}

export function shellNavigationLabels(canManage: boolean) {
  const navigation = navigationFor(canManage);
  return {
    primary: navigation.primaryNavigation.map((item) => item.railLabel ?? item.label),
    mobile: navigation.primaryNavigation.map((item) => item.label),
    more: navigation.moreNavigation.map((item) => item.label)
  };
}

export function AppShell({ session, view, setView, opsMode, setOpsMode, logout, connection, actionCount, privateMode = true, privateModeLocked = false, setPrivateMode = () => undefined, children }: {
  session: Session;
  view: AppView;
  setView: (view: AppView) => void;
  opsMode: OpsWorkspaceMode;
  setOpsMode: (mode: OpsWorkspaceMode) => void;
  logout: () => void;
  connection: "live" | "refreshing";
  actionCount: number;
  privateMode?: boolean;
  privateModeLocked?: boolean;
  setPrivateMode?: (enabled: boolean) => void;
  children: ReactNode;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const canManage = session.member.role === "OWNER" || session.member.role === "MANAGER";
  const { primaryNavigation, moreNavigation } = navigationFor(canManage);
  const secondaryActive = moreNavigation.some((item) => item.id === view);

  const navigate = (next: AppView) => {
    setMoreOpen(false);
    if (next === "ops") {
      setOpsMode("today");
      return;
    }
    setView(next);
  };

  const openOpsMode = (next: OpsWorkspaceMode) => {
    setMoreOpen(false);
    setOpsMode(next);
  };

  useEffect(() => {
    if (!moreOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [moreOpen]);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true"><span /></span>
          <div><strong>Production desk</strong><small>{session.workspace.name}</small></div>
        </div>
        <nav className="primary-nav" aria-label="Main navigation">
          {primaryNavigation.map(({ id, label, railLabel, icon: Icon }) => (
            <button key={id} className={view === "ops" && opsMode === id ? "is-active" : ""} type="button" onClick={() => openOpsMode(id)} aria-current={view === "ops" && opsMode === id ? "page" : undefined} title={railLabel ?? label}>
              <Icon size={19} strokeWidth={1.8} aria-hidden="true" /><span>{railLabel ?? label}</span>{id === "approvals" && actionCount > 0 ? <small className="nav-count">{actionCount > 99 ? "99+" : actionCount}</small> : null}
            </button>
          ))}
          <div className="desktop-more">
            <button className={secondaryActive || moreOpen ? "is-active" : ""} type="button" onClick={() => setMoreOpen((open) => !open)} aria-expanded={moreOpen} aria-controls="desktop-more-menu">
              <MoreHorizontal size={19} aria-hidden="true" /><span>More</span><ChevronDown className="nav-chevron" size={15} aria-hidden="true" />
            </button>
            {moreOpen ? <MoreMenu id="desktop-more-menu" items={moreNavigation} view={view} navigate={navigate} /> : null}
          </div>
        </nav>
        <div className="sidebar__bottom">
          <div className="profile-row profile-row--static">
            <Avatar name={session.member.displayName} />
            <span><strong>{session.member.displayName}</strong><small>{session.member.role.toLowerCase()}</small></span>
          </div>
          <button className="logout-button" type="button" onClick={logout}><LogOut size={17} />Sign out</button>
        </div>
      </aside>

      <div className="workspace-shell">
        <header className="topbar">
          <div className="topbar__context">
            <strong>{view === "ops" ? primaryNavigation.find((item) => item.id === opsMode)?.label ?? "Ops Agent" : VIEW_LABELS[view]}</strong>
            <span><i className={`live-dot ${connection === "refreshing" ? "is-refreshing" : ""}`} />{connection === "live" ? "Workspace live" : "Updating workspace"}</span>
          </div>
          <div className="topbar__actions">
            <button className={"private-mode-toggle private-mode-toggle--topbar " + (privateMode ? "is-active" : "")} type="button" aria-label="Private mode" aria-pressed={privateMode} disabled={privateModeLocked} title={privateModeLocked ? "Private mode is fixed until the current Voice request finishes." : privateMode ? "Private mode is on. Show phone numbers." : "Private mode is off. Mask phone numbers."} onClick={() => setPrivateMode(!privateMode)}>
              {privateMode ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}<span>Private</span><small>{privateMode ? "On" : "Off"}</small>
            </button>
            <div className="topbar-more">
              <button type="button" onClick={() => setMoreOpen((open) => !open)} aria-expanded={moreOpen} aria-controls="mobile-more-menu" aria-label="Open workspace menu"><MoreHorizontal size={20} aria-hidden="true" /></button>
              {/* WHY [BOL-COMPACT-NAV-ESCAPE-001]: route changes replace the
                  current history entry, so browser Back cannot be the only way
                  out of a secondary phone view. Keep an explicit Ops Agent home
                  and Sign out in this compact menu without changing desktop
                  navigation. Falsifier: a 360px secondary view strands the operator. */}
              {moreOpen ? <CompactMoreMenu id="mobile-more-menu" canManage={canManage} view={view} navigate={navigate} logout={() => { setMoreOpen(false); logout(); }} /> : null}
            </div>
          </div>
        </header>
        <main className="workspace">{children}</main>
      </div>

      <nav className="mobile-nav" aria-label="Ops Agent tools">
        {primaryNavigation.map(({ id, label, icon: Icon }) => (
          <button key={id} className={view === "ops" && opsMode === id ? "is-active" : ""} type="button" onClick={() => openOpsMode(id)} aria-current={view === "ops" && opsMode === id ? "page" : undefined}>
            <span className="mobile-nav__icon"><Icon size={20} aria-hidden="true" />{id === "approvals" && actionCount > 0 ? <small className="nav-count">{actionCount > 99 ? "99+" : actionCount}</small> : null}</span><span>{label}</span>
          </button>
        ))}
        <button className={"private-mode-toggle--mobile " + (privateMode ? "is-active" : "")} type="button" aria-label="Private mode" aria-pressed={privateMode} disabled={privateModeLocked} title={privateModeLocked ? "Private mode is fixed until the current Voice request finishes." : undefined} onClick={() => setPrivateMode(!privateMode)}>
          <span className="mobile-nav__icon">{privateMode ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}</span><span>Private</span>
        </button>
      </nav>
    </div>
  );
}

export function CompactMoreMenu({ id, canManage, view, navigate, logout }: {
  id: string;
  canManage: boolean;
  view: AppView;
  navigate: (view: AppView) => void;
  logout: () => void;
}) {
  return <MoreMenu
      id={id}
      items={navigationFor(canManage).moreNavigation}
      view={view}
      navigate={navigate}
      includeHome
      logout={logout}
    />;
}

function MoreMenu({ id, items, view, navigate, includeHome = false, logout }: {
  id?: string;
  items: readonly NavigationItem[];
  view: AppView;
  navigate: (view: AppView) => void;
  includeHome?: boolean;
  logout?: () => void;
}) {
  return (
    <div id={id} className="more-menu">
      {includeHome ? <div className="more-menu__item more-menu__item--home">
        <button className={view === "ops" ? "is-active" : ""} type="button" onClick={() => navigate("ops")} aria-current={view === "ops" ? "page" : undefined}>
          <MessageSquareText size={19} aria-hidden="true" /><span>Ops Agent</span>
        </button>
      </div> : null}
      {items.map(({ id: itemId, label, icon: Icon }) => (
        <div className="more-menu__item" key={itemId}>
          <button className={view === itemId ? "is-active" : ""} type="button" onClick={() => navigate(itemId)} aria-current={view === itemId ? "page" : undefined}>
            <Icon size={19} aria-hidden="true" /><span>{label}</span>
          </button>
        </div>
      ))}
      {logout ? <div className="more-menu__item more-menu__item--signout">
        <button type="button" onClick={logout}><LogOut size={19} aria-hidden="true" /><span>Sign out</span></button>
      </div> : null}
    </div>
  );
}
